/*
 * Design: Swiss Precision × 和の余白
 * お問い合わせフォーム — プラン・ソリューションアイテム・課題タグ連動
 * スパム対策: ハニーポット + 時間チェック + 簡易計算認証
 * Mobile: フル幅入力、タッチターゲット48px、safe-area対応
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  CheckCircle2,
  AlertCircle,
  Building2,
  User,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  ChevronDown,
  X,
  Zap,
  Settings,
  Expand,
  Rocket,
  GraduationCap,
  Server,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { CONTACT_PARAMS_EVENT, type ContactParams } from "@/hooks/useScrollToContact";

/* ─── Constants ─── */

const PREFECTURES = [
  "北海道",
  "青森県", "岩手県", "宮城県", "秋田県", "山形県", "福島県",
  "茨城県", "栃木県", "群馬県", "埼玉県", "千葉県", "東京都", "神奈川県",
  "新潟県", "富山県", "石川県", "福井県", "山梨県", "長野県",
  "岐阜県", "静岡県", "愛知県", "三重県",
  "滋賀県", "京都府", "大阪府", "兵庫県", "奈良県", "和歌山県",
  "鳥取県", "島根県", "岡山県", "広島県", "山口県",
  "徳島県", "香川県", "愛媛県", "高知県",
  "福岡県", "佐賀県", "長崎県", "熊本県", "大分県", "宮崎県", "鹿児島県", "沖縄県",
];

const PAIN_TAGS = [
  { id: "tenki", label: "転記が多い", description: "手作業でのデータ転記に時間がかかる" },
  { id: "sashimodoshi", label: "差戻しが多い", description: "確認・修正の手戻りが頻発する" },
  { id: "shime", label: "締めが遅い", description: "月次・週次の集計が遅れがち" },
  { id: "dentatsu", label: "伝達が漏れる", description: "情報共有・通知が抜け落ちる" },
  { id: "yokotenkai", label: "横展開できない", description: "成功事例を他部署に広げられない" },
];

const SERVICE_PLANS = [
  { id: "starter", label: "AI業務自動化スターターパック", icon: Rocket, price: "80万円〜" },
  { id: "training", label: "現場主導DX・内製化支援プログラム", icon: GraduationCap, price: "150万円〜" },
  { id: "saas", label: "業種特化型AIエージェント", icon: Server, price: "50万円〜" },
];

const SOLUTION_SETS = [
  { id: "quick", label: "Quick Win（3アイテム）", icon: Zap, tagline: "現場が「回る」最低限" },
  { id: "standard", label: "Standard Ops（5アイテム）", icon: Settings, tagline: "現場が「回り続ける」運用まで" },
  { id: "scale", label: "Scale & Control（7アイテム）", icon: Expand, tagline: "会社が「増やせる」統制まで" },
];

const SOLUTION_ITEMS = [
  { id: "01", name: "メール自動起票" },
  { id: "02", name: "ファイル整流化" },
  { id: "03", name: "ドラフト生成" },
  { id: "04", name: "自動チェック" },
  { id: "05", name: "即時通知" },
  { id: "06", name: "スケジュール同期" },
  { id: "07", name: "KPI自動集計" },
  { id: "08", name: "例外キュー運用" },
  { id: "09", name: "権限・監査ログパック" },
  { id: "10", name: "テンプレ横展開" },
];

/* ─── Spam Protection ─── */

function generateMathChallenge(): { question: string; answer: number } {
  const a = Math.floor(Math.random() * 9) + 1;
  const b = Math.floor(Math.random() * 9) + 1;
  return { question: `${a} + ${b} = ?`, answer: a + b };
}

/* ─── URL Params Parser ─── */

function getContactParams(): {
  plan?: string;
  set?: string;
  items?: string[];
  pains?: string[];
  industry?: string;
} {
  const hash = window.location.hash;
  const queryStart = hash.indexOf("?");
  if (queryStart === -1) return {};
  const params = new URLSearchParams(hash.slice(queryStart + 1));
  return {
    plan: params.get("plan") || undefined,
    set: params.get("set") || undefined,
    items: params.get("items")?.split(",").filter(Boolean) || undefined,
    pains: params.get("pains")?.split(",").filter(Boolean) || undefined,
    industry: params.get("industry") || undefined,
  };
}

/* ─── Animation ─── */

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
};

/* ─── Component ─── */

export function ContactFormSection() {
  // Form state
  const [companyName, setCompanyName] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [prefecture, setPrefecture] = useState("");
  const [selectedPains, setSelectedPains] = useState<string[]>([]);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [selectedSet, setSelectedSet] = useState("");
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [message, setMessage] = useState("");
  const [prefectureOpen, setPrefectureOpen] = useState(false);

  // Spam protection
  const [honeypot, setHoneypot] = useState("");
  const [mathChallenge, setMathChallenge] = useState(() => generateMathChallenge());
  const [mathAnswer, setMathAnswer] = useState("");
  const [formLoadTime] = useState(() => Date.now());

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showItemPicker, setShowItemPicker] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);
  const prefRef = useRef<HTMLDivElement>(null);
  const [, navigate] = useLocation();

  // Close prefecture dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (prefRef.current && !prefRef.current.contains(e.target as Node)) {
        setPrefectureOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Parse URL params on mount
  useEffect(() => {
    const params = getContactParams();
    if (params.plan) setSelectedPlan(params.plan);
    if (params.set) setSelectedSet(params.set);
    if (params.items) setSelectedItems(params.items);
    if (params.pains) setSelectedPains(params.pains);
  }, []);

  // Listen for custom contact params events from CTA buttons
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<ContactParams>).detail;
      if (detail.plan) setSelectedPlan(detail.plan);
      if (detail.set) setSelectedSet(detail.set);
      if (detail.items) setSelectedItems(detail.items);
      if (detail.pains) setSelectedPains(detail.pains);
    };
    window.addEventListener(CONTACT_PARAMS_EVENT, handler);
    return () => window.removeEventListener(CONTACT_PARAMS_EVENT, handler);
  }, []);

  // tRPC mutation for form submission
  const submitMutation = trpc.contact.submit.useMutation();

  // Auto-select items when set is chosen
  const setItemMap: Record<string, string[]> = useMemo(() => ({
    quick: ["01", "05", "08"],
    standard: ["01", "05", "08", "02", "07"],
    scale: ["01", "05", "08", "02", "07", "04", "09"],
  }), []);

  useEffect(() => {
    if (selectedSet && setItemMap[selectedSet]) {
      setSelectedItems(setItemMap[selectedSet]);
    }
  }, [selectedSet, setItemMap]);

  const togglePain = useCallback((id: string) => {
    setSelectedPains(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  }, []);

  const toggleItem = useCallback((id: string) => {
    setSelectedItems(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
    // Clear set selection when manually changing items
    setSelectedSet("");
  }, []);

  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};
    if (!companyName.trim()) newErrors.companyName = "事業者名を入力してください";
    if (!contactName.trim()) newErrors.contactName = "担当者名を入力してください";
    if (!email.trim()) {
      newErrors.email = "メールアドレスを入力してください";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "正しいメールアドレスを入力してください";
    }
    if (!prefecture) newErrors.prefecture = "都道府県を選択してください";
    if (parseInt(mathAnswer) !== mathChallenge.answer) {
      newErrors.math = "計算結果が正しくありません";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [companyName, contactName, email, prefecture, mathAnswer, mathChallenge.answer]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot check
    if (honeypot) return;

    // Time check (must take at least 3 seconds)
    if (Date.now() - formLoadTime < 3000) return;

    if (!validate()) return;

    setIsSubmitting(true);

    try {
      // Build context info for message
      const painLabels = selectedPains.map(id => PAIN_TAGS.find(p => p.id === id)?.label).filter(Boolean);
      const planLabel = SERVICE_PLANS.find(p => p.id === selectedPlan)?.label || "";
      const setLabel = SOLUTION_SETS.find(s => s.id === selectedSet)?.label || "";
      const itemLabels = selectedItems.map(id => {
        const item = SOLUTION_ITEMS.find(i => i.id === id);
        return item ? `${item.id}: ${item.name}` : "";
      }).filter(Boolean);

      // Build full message with context
      const fullMessage = [
        `【都道府県】${prefecture}`,
        painLabels.length > 0 ? `【課題】${painLabels.join("、")}` : "",
        planLabel ? `【ご検討プラン】${planLabel}` : "",
        setLabel ? `【ソリューションセット】${setLabel}` : "",
        itemLabels.length > 0 ? `【ソリューションアイテム】${itemLabels.join(", ")}` : "",
        "",
        message ? `【ご相談内容】` : "",
        message || "",
      ].filter(line => line !== "").join("\n");

      // Submit via tRPC API
      const result = await submitMutation.mutateAsync({
        companyName: companyName || undefined,
        contactName,
        email,
        phone: phone || undefined,
        plan: selectedPlan || undefined,
        selectedSet: selectedSet || undefined,
        selectedItems: selectedItems.length > 0 ? selectedItems.join(",") : undefined,
        message: fullMessage,
        honeypot: honeypot || undefined,
        formLoadedAt: formLoadTime,
      });

      if (result.success) {
        setIsSubmitted(true);

        // Build thanks page URL with summary params
        const thanksParams = new URLSearchParams();
        if (companyName) thanksParams.set("company", companyName);
        if (planLabel) thanksParams.set("plan", planLabel);
        if (setLabel) thanksParams.set("set", setLabel);
        const thanksQuery = thanksParams.toString();

        // Navigate to thanks page
        setTimeout(() => {
          navigate(`/thanks${thanksQuery ? `?${thanksQuery}` : ""}`);
        }, 1500);
      }
    } catch (error) {
      console.error("[Contact] Submit failed:", error);
      setErrors({ submit: "送信に失敗しました。しばらくしてから再度お試しください。" });
    } finally {
      setIsSubmitting(false);
    }
  }, [honeypot, formLoadTime, validate, companyName, contactName, email, phone, prefecture, selectedPains, selectedPlan, selectedSet, selectedItems, message, submitMutation, navigate]);

  if (isSubmitted) {
    return (
      <section id="contact-form" className="py-14 sm:py-20 lg:py-28 bg-[#F9F9F9]">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-2xl mx-auto text-center"
          >
            <div className="w-16 h-16 mx-auto mb-6 bg-[#E03030] rounded-full flex items-center justify-center">
              <CheckCircle2 className="text-white" size={32} />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-[#1A1A1A] mb-4">
              お問い合わせを受け付けました
            </h3>
            <p className="text-sm sm:text-base text-[#333333] leading-relaxed mb-6">
              お問い合わせいただき、ありがとうございます。<br />
              通常、1営業日以内に担当者よりご連絡いたします。<br />
              お急ぎの場合は <a href="tel:05050521301" className="text-[#E03030] font-medium underline">050-5052-1301</a> までお電話ください。
            </p>
            <button
              onClick={() => {
                setIsSubmitted(false);
                setMathChallenge(generateMathChallenge());
                setMathAnswer("");
              }}
              className="inline-flex items-center gap-2 px-6 py-3 border border-[#E0E0E0] text-[#333333] text-sm font-medium rounded hover:border-[#E03030] hover:text-[#E03030] transition-colors"
            >
              もう一度入力する
            </button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact-form" className="py-14 sm:py-20 lg:py-28 bg-[#F9F9F9]">
      <div className="container">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div {...fadeInUp} transition={{ duration: 0.6 }} className="mb-8 sm:mb-12">
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <div className="w-1 h-6 sm:h-8 bg-[#E03030]" />
              <span
                className="text-[10px] sm:text-xs font-medium tracking-widest uppercase text-[#666666]"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Contact Form
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl lg:text-4xl font-bold text-[#1A1A1A] mb-3 sm:mb-4">
              お問い合わせ・ご相談
            </h2>
            <p className="text-sm sm:text-base text-[#333333] leading-relaxed max-w-2xl">
              課題やご検討中のプランを選択するだけで、お問い合わせ内容が自動的に整理されます。
              <br className="hidden sm:block" />
              お気軽にご相談ください。
            </p>
          </motion.div>

          <form ref={formRef} onSubmit={handleSubmit} noValidate>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
              {/* ─── Left: 課題・プラン選択 (2/5) ─── */}
              <motion.div
                {...fadeInUp}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="lg:col-span-2 space-y-6"
              >
                {/* 課題タグ選択 */}
                <div className="bg-white border border-[#E0E0E0] p-4 sm:p-6">
                  <h3 className="text-sm font-bold text-[#1A1A1A] mb-1">
                    現在の課題感
                  </h3>
                  <p className="text-[10px] sm:text-xs text-[#666666] mb-4">
                    該当するものをすべて選択（任意）
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {PAIN_TAGS.map(tag => (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => togglePain(tag.id)}
                        className={`px-3 py-2 text-xs sm:text-sm font-medium border transition-all duration-200 touch-manipulation ${
                          selectedPains.includes(tag.id)
                            ? "bg-[#E03030] text-white border-[#E03030]"
                            : "bg-white text-[#333333] border-[#E0E0E0] hover:border-[#E03030] hover:text-[#E03030]"
                        }`}
                      >
                        {tag.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* サービスプラン選択 */}
                <div className="bg-white border border-[#E0E0E0] p-4 sm:p-6">
                  <h3 className="text-sm font-bold text-[#1A1A1A] mb-1">
                    ご検討中のプラン
                  </h3>
                  <p className="text-[10px] sm:text-xs text-[#666666] mb-4">
                    1つ選択（任意）
                  </p>
                  <div className="space-y-2">
                    {SERVICE_PLANS.map(plan => {
                      const Icon = plan.icon;
                      return (
                        <button
                          key={plan.id}
                          type="button"
                          onClick={() => setSelectedPlan(prev => prev === plan.id ? "" : plan.id)}
                          className={`w-full flex items-center gap-3 p-3 text-left border transition-all duration-200 touch-manipulation ${
                            selectedPlan === plan.id
                              ? "bg-[#FEF2F2] border-[#E03030] text-[#1A1A1A]"
                              : "bg-white border-[#E0E0E0] text-[#333333] hover:border-[#999]"
                          }`}
                        >
                          <div className={`w-8 h-8 flex items-center justify-center shrink-0 ${
                            selectedPlan === plan.id ? "text-[#E03030]" : "text-[#666666]"
                          }`}>
                            <Icon size={18} />
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs sm:text-sm font-medium truncate">{plan.label}</div>
                            <div className="text-[10px] text-[#666666]">{plan.price}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* ソリューションセット選択 */}
                <div className="bg-white border border-[#E0E0E0] p-4 sm:p-6">
                  <h3 className="text-sm font-bold text-[#1A1A1A] mb-1">
                    ソリューションセット
                  </h3>
                  <p className="text-[10px] sm:text-xs text-[#666666] mb-4">
                    セットを選ぶとアイテムが自動選択されます（任意）
                  </p>
                  <div className="space-y-2">
                    {SOLUTION_SETS.map(set => {
                      const Icon = set.icon;
                      return (
                        <button
                          key={set.id}
                          type="button"
                          onClick={() => setSelectedSet(prev => prev === set.id ? "" : set.id)}
                          className={`w-full flex items-center gap-3 p-3 text-left border transition-all duration-200 touch-manipulation ${
                            selectedSet === set.id
                              ? "bg-[#FEF2F2] border-[#E03030] text-[#1A1A1A]"
                              : "bg-white border-[#E0E0E0] text-[#333333] hover:border-[#999]"
                          }`}
                        >
                          <div className={`w-8 h-8 flex items-center justify-center shrink-0 ${
                            selectedSet === set.id ? "text-[#E03030]" : "text-[#666666]"
                          }`}>
                            <Icon size={18} />
                          </div>
                          <div className="min-w-0">
                            <div className="text-xs sm:text-sm font-medium">{set.label}</div>
                            <div className="text-[10px] text-[#666666]">{set.tagline}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* アイテム個別選択 */}
                  <button
                    type="button"
                    onClick={() => setShowItemPicker(!showItemPicker)}
                    className="mt-3 w-full flex items-center justify-between px-3 py-2 text-xs text-[#666666] border border-dashed border-[#E0E0E0] hover:border-[#999] transition-colors touch-manipulation"
                  >
                    <span>
                      {selectedItems.length > 0
                        ? `${selectedItems.length}個のアイテムを選択中`
                        : "アイテムを個別に選択する"}
                    </span>
                    <ChevronDown
                      size={14}
                      className={`transition-transform ${showItemPicker ? "rotate-180" : ""}`}
                    />
                  </button>

                  <AnimatePresence>
                    {showItemPicker && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="mt-3 grid grid-cols-1 gap-1.5">
                          {SOLUTION_ITEMS.map(item => (
                            <label
                              key={item.id}
                              className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer border transition-all touch-manipulation ${
                                selectedItems.includes(item.id)
                                  ? "bg-[#FEF2F2] border-[#E03030]/30"
                                  : "bg-white border-transparent hover:bg-[#F9F9F9]"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedItems.includes(item.id)}
                                onChange={() => toggleItem(item.id)}
                                className="w-4 h-4 accent-[#E03030] shrink-0"
                              />
                              <span className="text-xs sm:text-sm text-[#333333]">
                                <span className="font-medium text-[#666666]" style={{ fontFamily: "'Inter', sans-serif" }}>
                                  {item.id}
                                </span>
                                {" "}{item.name}
                              </span>
                            </label>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>

              {/* ─── Right: 基本情報入力 (3/5) ─── */}
              <motion.div
                {...fadeInUp}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="lg:col-span-3"
              >
                <div className="bg-white border border-[#E0E0E0] p-4 sm:p-6 lg:p-8">
                  <h3 className="text-sm font-bold text-[#1A1A1A] mb-6 flex items-center gap-2">
                    <div className="w-1 h-4 bg-[#E03030]" />
                    お客様情報
                  </h3>

                  <div className="space-y-5">
                    {/* 事業者名 */}
                    <div>
                      <label className="flex items-center gap-1.5 text-xs sm:text-sm font-medium text-[#1A1A1A] mb-2">
                        <Building2 size={14} className="text-[#666666]" />
                        事業者名
                        <span className="text-[#E03030] text-[10px]">必須</span>
                      </label>
                      <input
                        type="text"
                        value={companyName}
                        onChange={e => { setCompanyName(e.target.value); setErrors(prev => ({ ...prev, companyName: "" })); }}
                        placeholder="例：株式会社〇〇"
                        className={`w-full px-4 py-3 text-sm border bg-white focus:outline-none focus:ring-2 focus:ring-[#E03030]/20 focus:border-[#E03030] transition-colors ${
                          errors.companyName ? "border-[#E03030]" : "border-[#E0E0E0]"
                        }`}
                      />
                      {errors.companyName && (
                        <p className="mt-1 text-[10px] sm:text-xs text-[#E03030] flex items-center gap-1">
                          <AlertCircle size={12} /> {errors.companyName}
                        </p>
                      )}
                    </div>

                    {/* 担当者名 */}
                    <div>
                      <label className="flex items-center gap-1.5 text-xs sm:text-sm font-medium text-[#1A1A1A] mb-2">
                        <User size={14} className="text-[#666666]" />
                        担当者名
                        <span className="text-[#E03030] text-[10px]">必須</span>
                      </label>
                      <input
                        type="text"
                        value={contactName}
                        onChange={e => { setContactName(e.target.value); setErrors(prev => ({ ...prev, contactName: "" })); }}
                        placeholder="例：山田 太郎"
                        className={`w-full px-4 py-3 text-sm border bg-white focus:outline-none focus:ring-2 focus:ring-[#E03030]/20 focus:border-[#E03030] transition-colors ${
                          errors.contactName ? "border-[#E03030]" : "border-[#E0E0E0]"
                        }`}
                      />
                      {errors.contactName && (
                        <p className="mt-1 text-[10px] sm:text-xs text-[#E03030] flex items-center gap-1">
                          <AlertCircle size={12} /> {errors.contactName}
                        </p>
                      )}
                    </div>

                    {/* メールアドレス */}
                    <div>
                      <label className="flex items-center gap-1.5 text-xs sm:text-sm font-medium text-[#1A1A1A] mb-2">
                        <Mail size={14} className="text-[#666666]" />
                        メールアドレス
                        <span className="text-[#E03030] text-[10px]">必須</span>
                      </label>
                      <input
                        type="email"
                        value={email}
                        onChange={e => { setEmail(e.target.value); setErrors(prev => ({ ...prev, email: "" })); }}
                        placeholder="例：info@example.co.jp"
                        className={`w-full px-4 py-3 text-sm border bg-white focus:outline-none focus:ring-2 focus:ring-[#E03030]/20 focus:border-[#E03030] transition-colors ${
                          errors.email ? "border-[#E03030]" : "border-[#E0E0E0]"
                        }`}
                      />
                      {errors.email && (
                        <p className="mt-1 text-[10px] sm:text-xs text-[#E03030] flex items-center gap-1">
                          <AlertCircle size={12} /> {errors.email}
                        </p>
                      )}
                    </div>

                    {/* 電話番号（任意） + 都道府県 */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                      <div>
                        <label className="flex items-center gap-1.5 text-xs sm:text-sm font-medium text-[#1A1A1A] mb-2">
                          <Phone size={14} className="text-[#666666]" />
                          電話番号
                          <span className="text-[#666666] text-[10px]">任意</span>
                        </label>
                        <input
                          type="tel"
                          value={phone}
                          onChange={e => setPhone(e.target.value)}
                          placeholder="例：050-1234-5678"
                          className="w-full px-4 py-3 text-sm border border-[#E0E0E0] bg-white focus:outline-none focus:ring-2 focus:ring-[#E03030]/20 focus:border-[#E03030] transition-colors"
                        />
                      </div>

                      {/* 都道府県 */}
                      <div ref={prefRef}>
                        <label className="flex items-center gap-1.5 text-xs sm:text-sm font-medium text-[#1A1A1A] mb-2">
                          <MapPin size={14} className="text-[#666666]" />
                          都道府県
                          <span className="text-[#E03030] text-[10px]">必須</span>
                        </label>
                        <div className="relative">
                          <button
                            type="button"
                            onClick={() => setPrefectureOpen(!prefectureOpen)}
                            className={`w-full flex items-center justify-between px-4 py-3 text-sm border bg-white focus:outline-none focus:ring-2 focus:ring-[#E03030]/20 focus:border-[#E03030] transition-colors touch-manipulation ${
                              errors.prefecture ? "border-[#E03030]" : "border-[#E0E0E0]"
                            }`}
                          >
                            <span className={prefecture ? "text-[#1A1A1A]" : "text-[#999]"}>
                              {prefecture || "選択してください"}
                            </span>
                            <ChevronDown
                              size={16}
                              className={`text-[#666666] transition-transform ${prefectureOpen ? "rotate-180" : ""}`}
                            />
                          </button>
                          <AnimatePresence>
                            {prefectureOpen && (
                              <motion.div
                                initial={{ opacity: 0, y: -4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -4 }}
                                transition={{ duration: 0.15 }}
                                className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-[#E0E0E0] shadow-lg max-h-60 overflow-y-auto"
                              >
                                {PREFECTURES.map(pref => (
                                  <button
                                    key={pref}
                                    type="button"
                                    onClick={() => {
                                      setPrefecture(pref);
                                      setPrefectureOpen(false);
                                      setErrors(prev => ({ ...prev, prefecture: "" }));
                                    }}
                                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[#F9F9F9] transition-colors touch-manipulation ${
                                      prefecture === pref ? "bg-[#FEF2F2] text-[#E03030] font-medium" : "text-[#333333]"
                                    }`}
                                  >
                                    {pref}
                                  </button>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                        {errors.prefecture && (
                          <p className="mt-1 text-[10px] sm:text-xs text-[#E03030] flex items-center gap-1">
                            <AlertCircle size={12} /> {errors.prefecture}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* ご相談内容（任意） */}
                    <div>
                      <label className="flex items-center gap-1.5 text-xs sm:text-sm font-medium text-[#1A1A1A] mb-2">
                        <MessageSquare size={14} className="text-[#666666]" />
                        ご相談内容
                        <span className="text-[#666666] text-[10px]">任意</span>
                      </label>
                      <textarea
                        value={message}
                        onChange={e => setMessage(e.target.value)}
                        placeholder="具体的な課題やご要望があればお書きください"
                        rows={4}
                        className="w-full px-4 py-3 text-sm border border-[#E0E0E0] bg-white focus:outline-none focus:ring-2 focus:ring-[#E03030]/20 focus:border-[#E03030] transition-colors resize-none"
                      />
                    </div>

                    {/* 選択内容サマリー */}
                    {(selectedPains.length > 0 || selectedPlan || selectedSet || selectedItems.length > 0) && (
                      <div className="bg-[#F9F9F9] border border-[#E0E0E0] p-4">
                        <h4 className="text-xs font-bold text-[#1A1A1A] mb-3">選択内容の確認</h4>
                        <div className="space-y-2 text-xs text-[#333333]">
                          {selectedPains.length > 0 && (
                            <div className="flex gap-2">
                              <span className="text-[#666666] shrink-0 w-16">課題：</span>
                              <span>{selectedPains.map(id => PAIN_TAGS.find(p => p.id === id)?.label).join("、")}</span>
                            </div>
                          )}
                          {selectedPlan && (
                            <div className="flex gap-2">
                              <span className="text-[#666666] shrink-0 w-16">プラン：</span>
                              <span>{SERVICE_PLANS.find(p => p.id === selectedPlan)?.label}</span>
                            </div>
                          )}
                          {selectedSet && (
                            <div className="flex gap-2">
                              <span className="text-[#666666] shrink-0 w-16">セット：</span>
                              <span>{SOLUTION_SETS.find(s => s.id === selectedSet)?.label}</span>
                            </div>
                          )}
                          {selectedItems.length > 0 && (
                            <div className="flex gap-2">
                              <span className="text-[#666666] shrink-0 w-16">アイテム：</span>
                              <div className="flex flex-wrap gap-1">
                                {selectedItems.map(id => {
                                  const item = SOLUTION_ITEMS.find(i => i.id === id);
                                  return item ? (
                                    <span key={id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-[#E0E0E0] text-[10px]">
                                      <span className="font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>{item.id}</span>
                                      {item.name}
                                    </span>
                                  ) : null;
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Honeypot (hidden from humans) */}
                    <div className="absolute -left-[9999px] opacity-0 h-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
                      <label>
                        Leave this empty
                        <input
                          type="text"
                          name="website_url"
                          value={honeypot}
                          onChange={e => setHoneypot(e.target.value)}
                          tabIndex={-1}
                          autoComplete="off"
                        />
                      </label>
                    </div>

                    {/* 簡易計算認証 */}
                    <div className="bg-[#F9F9F9] border border-[#E0E0E0] p-4">
                      <label className="flex items-center gap-1.5 text-xs sm:text-sm font-medium text-[#1A1A1A] mb-2">
                        ロボットでないことを確認
                        <span className="text-[#E03030] text-[10px]">必須</span>
                      </label>
                      <div className="flex items-center gap-3">
                        <span className="text-base sm:text-lg font-bold text-[#1A1A1A] whitespace-nowrap" style={{ fontFamily: "'Inter', sans-serif" }}>
                          {mathChallenge.question}
                        </span>
                        <input
                          type="number"
                          value={mathAnswer}
                          onChange={e => { setMathAnswer(e.target.value); setErrors(prev => ({ ...prev, math: "" })); }}
                          placeholder="答え"
                          className={`w-24 px-3 py-2.5 text-sm text-center border bg-white focus:outline-none focus:ring-2 focus:ring-[#E03030]/20 focus:border-[#E03030] transition-colors ${
                            errors.math ? "border-[#E03030]" : "border-[#E0E0E0]"
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => { setMathChallenge(generateMathChallenge()); setMathAnswer(""); }}
                          className="text-xs text-[#666666] hover:text-[#E03030] transition-colors whitespace-nowrap touch-manipulation"
                        >
                          別の問題
                        </button>
                      </div>
                      {errors.math && (
                        <p className="mt-1 text-[10px] sm:text-xs text-[#E03030] flex items-center gap-1">
                          <AlertCircle size={12} /> {errors.math}
                        </p>
                      )}
                    </div>

                    {/* Submit */}
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full inline-flex items-center justify-center gap-2 sm:gap-3 px-6 sm:px-8 py-4 sm:py-5 bg-[#E03030] text-white font-bold text-sm sm:text-base rounded hover:bg-[#c72828] transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed active:scale-[0.98] touch-manipulation"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          送信準備中...
                        </>
                      ) : (
                        <>
                          <Send size={16} />
                          この内容で相談する
                        </>
                      )}
                    </button>

                    <p className="text-[10px] sm:text-xs text-[#666666] text-center">
                      送信ボタンを押すと、お問い合わせ内容が直接送信されます。通常1営業日以内にご連絡いたします。
                    </p>

                    {errors.submit && (
                      <p className="text-xs text-[#E03030] text-center flex items-center justify-center gap-1">
                        <AlertCircle size={14} />
                        {errors.submit}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
